import React, { useState, memo, useEffect } from 'react';
import { useTranslation } from "core/hooks/useTranslation";
import { useLocation } from "react-router-dom";

import { Row, Col } from "core/ui-kit/layout";
import Form from "core/ui-kit/form";
import Modal from "core/ui-kit/modal";

import { POPUP_SIZE } from "core/constants/common";

import { isFormChanged } from "utils/form";

import { FORM_FIELD_NAMES } from './constants';

import brandService from 'core/services/apiServices/brandService';
import showSuccess from 'core/helpers/common/showSuccess';
import FormRulesBuilder from 'core/helpers/common/formRulesBuilder';

import NAME_REGEXP from 'core/constants/regexp/nameRegexp';
import URL_REGEXP from 'core/constants/regexp/urlRegexp';

const AddEditMobileUrl = ({
    isVisible,
    mobileUrl,
    onSave,
    onClose
}) => {
    const { t } = useTranslation();
    const [formInstance] = Form.useForm();

    const { search } = useLocation();

    /** Is Edit Mode */
    const editMode = mobileUrl !== null;

    const [isFormTouched, setIsFormTouched] = useState(!editMode);

    const [isSaving, setIsSaving] = useState(false);

    const brandId = (new URLSearchParams(search)).get("brandId");

    const defaultValues = editMode ? {
        [FORM_FIELD_NAMES.URL]: mobileUrl.url,
        [FORM_FIELD_NAMES.NAME]: mobileUrl.name
    } : {
        [FORM_FIELD_NAMES.URL]: "",
        [FORM_FIELD_NAMES.NAME]: ""
    };

    const onOkButtonClick = () => {
        formInstance.submit()
    }

    const handleValuesChange = (_, formValues) => {
        setIsFormTouched(isFormChanged({ ...formValues }, { ...defaultValues }))
    }

    const onSuccess = ({ value, message }) => {
        showSuccess(message);
        onSave(value, editMode);
        onClose();
    }

    const handleFormFinish = formData => {
        setIsSaving(true);
        const d = {
            url: formData[FORM_FIELD_NAMES.URL],
            name: formData[FORM_FIELD_NAMES.NAME]
        }

        if(editMode){
            d.id = mobileUrl.id;
        } else {
            d.brandId = brandId;
        }

        if(editMode){
            brandService.saveMobileUrl(d)
                .then(onSuccess)
                .finally(() => setIsSaving(false))
        } else {
            brandService.addMobileUrl(d)
                .then(onSuccess)
                .finally(() => setIsSaving(false))
        }
    };

    useEffect(() => {
        if(isVisible){
            formInstance.setFieldsValue(defaultValues);
            setIsFormTouched(!editMode);
        }
    }, [isVisible])

    return (
        <Modal
            open={isVisible}
            title={editMode ? t('backoffice.projects.editMobileUrl') : t('backoffice.projects.addMobileUrl')}
            cancelText={t('backoffice.common.cancel')}
            okText={editMode ? t('backoffice.common.save') : t('backoffice.common.add')}
            onOk={onOkButtonClick}
            onCancel={onClose}
            size={POPUP_SIZE.SMALL}
            okButtonProps={{ disabled: !isFormTouched }}
            isLoading={isSaving}
        >
            <Form
                form={formInstance}
                onValuesChange={ editMode ? handleValuesChange : undefined}
                onFinish={handleFormFinish}
            >
                <Row gutter={[16, 0]}>
                    <Col span={24}>
                        <Form.Input
                            label={t('backoffice.projects.name')}
                            placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.name')}`}
                            name={FORM_FIELD_NAMES.NAME}
                            rules={
                                new FormRulesBuilder(t).required().minCharactersCount(2).maxCharactersCount(30).regexp(NAME_REGEXP).build()
                            }
                            maxLength={30}
                        />
                    </Col>
                    <Col span={24}>
                        <Form.Input
                            label={t('backoffice.projects.mobileUrl')}
                            placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.mobileUrl')}`}
                            name={FORM_FIELD_NAMES.URL}
                            rules={
                                new FormRulesBuilder(t).required().maxCharactersCount(100).regexp(URL_REGEXP).build()
                            }
                            maxLength={100}
                            className="rt--form-item-without-margin"
                        />
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}

export default memo(AddEditMobileUrl);